import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Building2, Check, Warehouse, X } from "lucide-react";

const SERVICES = [
  {
    id: "home-shifting",
    title: "Home Shifting",
    span: "bento-lg",
    img: "https://images.unsplash.com/photo-1714647211902-bb711d643a17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxwYWNraW5nJTIwaG91c2Vob2xkJTIwZnVybml0dXJlJTIwaG9tZSUyMG1vdmluZyUyMGJveGVzJTIwbW92ZXJzfGVufDB8fHx8MTc5MTc0MjgxNHww&ixlib=rb-4.1.0&q=85",
    price: "from ₹4,999",
    tagline: "Every room, every item — packed, moved and placed with care.",
    details: ["Premium packing material for every item", "Furniture disassembly & reassembly", "Door-to-door, same-week scheduling", "Unpacking and placement at your new home"],
  },
  {
    id: "car-carriers",
    title: "Car Carriers",
    span: "bento-wide",
    img: "https://images.unsplash.com/photo-1761917904658-2a9ecb84a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjYXIlMjB0cmFuc3BvcnQlMjBjYXJyaWVyJTIwdHJ1Y2slMjBzaGlwcGluZ3xlbnwwfHx8fDE3OTE3NDI3NDZ8MA&ixlib=rb-4.1.0&q=85",
    price: "from ₹3,499",
    tagline: "Enclosed carriers, doorstep pickup, zero scratches.",
    details: ["Enclosed & open carrier options", "GPS-tracked journey updates", "Doorstep pickup and delivery", "Transit insurance available"],
  },
  {
    id: "office-relocation",
    title: "Office Relocation",
    span: "",
    icon: Building2,
    price: "from ₹9,999",
    tagline: "Move your team without losing a workday.",
    details: ["Weekend & overnight moves", "IT equipment handled separately", "Labelled inventory for every desk", "Planned floor-by-floor setup"],
  },
  {
    id: "storage",
    title: "Storage & Warehousing",
    span: "",
    icon: Warehouse,
    price: "from ₹999/mo",
    tagline: "Safe, insured space for as long as you need.",
    details: ["CCTV-monitored facility", "Pest-controlled & weather-safe", "Flexible short or long terms", "Full inventory on check-in"],
  },
];

export const Services = () => {
  const [active, setActive] = useState(null);

  return (
    <section className="section services-section" id="services">
      <div className="container">
        <div className="section-heading">
          <div>
            <p className="overline">What we do</p>
            <h2>Four ways we <em>carry your world.</em></h2>
          </div>
          <p className="section-sub">Tap any service to see exactly what's included — and what it starts at.</p>
        </div>

        <div className="bento-grid">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.article
                key={service.id}
                className={`bento-card ${service.span} ${service.img ? "has-img" : ""}`}
                data-testid={`service-card-${i + 1}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setActive(service)}
              >
                {service.img && <img src={service.img} alt={service.title} loading="lazy" />}
                <div className="bento-content">
                  <span className="bento-price">{service.price}</span>
                  <div>
                    {Icon && <span className="bento-icon"><Icon size={22} strokeWidth={1.8} /></span>}
                    <h3>{service.title}</h3>
                    <p>{service.tagline}</p>
                  </div>
                </div>
                <span className="bento-arrow" data-testid={`service-link-${i + 1}`}><ArrowUpRight size={17} /></span>
              </motion.article>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div className="service-modal-backdrop" data-testid="service-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}>
            <motion.div className="service-modal"
              initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}>
              {active.img && <div className="service-modal-img"><img src={active.img} alt={active.title} /></div>}
              <div className="service-modal-body">
                <p className="overline">{active.price}</p>
                <h3 data-testid="service-modal-title">{active.title}</h3>
                <p className="service-modal-tagline">{active.tagline}</p>
                <ul>
                  {active.details.map((d) => <li key={d}><Check size={15} /> {d}</li>)}
                </ul>
                <a className="btn btn-dark" data-testid="service-modal-quote-button" href="#quote" onClick={() => setActive(null)}>
                  Get my {active.title.toLowerCase()} estimate <ArrowUpRight size={15} />
                </a>
              </div>
              <button className="service-modal-close" data-testid="service-modal-close" onClick={() => setActive(null)} aria-label="Close service details">
                <X size={18} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
